import { ref } from 'vue'
import { SHAPES } from '@/lib/CanvasEngine/const'

// 인증이 필요 없는 경로들
export const PUBLIC_PATHS = ['/login', '/public', '/demo']

// 기기에 도면 타일(동적스케일) 이미지들이 저장되는 디렉토리명
export const TILE_DIR = 'tile'

// 기기에 사진(시설물, 결함)들이 저장되는 디렉토리명
export const PHOTO_DIR = 'zentrix_photos'

export const shapeColorItems = [
  {
    name: 'none',
    color: 'rgba(255, 255, 255, 1)',
  },
  {
    name: 'red',
    color: 'rgba(255, 0, 0, 1)',
  },
  {
    name: 'blue',
    color: 'rgba(0, 0, 255, 1)',
  },
  {
    name: 'green',
    color: 'rgba(0, 128, 0, 1)',
  },
  {
    name: 'cyan',
    color: 'rgba(100, 205, 220, 1)',
  },
  {
    name: 'black',
    color: 'rgba(0, 0, 0, 1)',
  },
  {
    name: 'yellow',
    color: 'rgba(255, 140, 0, 1)',
  },
  {
    name: 'purple',
    color: 'rgba(106, 0, 255, 1)',
  },
]

export const removePink = (color: string): string => {
  if (color === 'rgba(238, 1, 164, 1)') {
    return 'rgba(106, 0, 255, 1)'
  }
  return color
}

export const getCurrentColor = (name: string): string => {
  const findColor = shapeColorItems.find(item => item.name == name)
  if (!findColor) return ''
  return findColor.color
}

export const getCurrentColorName = (color: string): string => {
  const findColor = shapeColorItems.find(item => item.color == color)
  if (!findColor) return ''
  return findColor.name
}

// 도형 색상 팔레트 노출 순서 (전차 전용 보라는 선택 대상에서 제외)
const SHAPE_PALETTE_ORDER = ['red', 'blue', 'green', 'cyan', 'black', 'yellow', 'none']
export const shapePaletteColors = SHAPE_PALETTE_ORDER.flatMap(name => {
  const found = shapeColorItems.find(item => item.name === name)
  return found ? [found] : []
})

// 투명 채우기에 사용하는 색상 값
export const TRANSPARENT_FILL_COLOR = 'rgba(255, 255, 255, 0)'

// 색상 값이 투명(알파 0)인지 여부
export const isTransparentColor = (color: string | undefined): boolean => {
  if (!color) return false
  const matched = color.replace(/\s/g, '').match(/^rgba\(\d+,\d+,\d+,(\d*\.?\d+)\)$/)
  return matched ? Number(matched[1]) === 0 : false
}

// 전차(previous) 결함의 스타일 상수
export const PREV_DEFECT_STYLE = {
  // 테두리 색상 shape_data.color, color
  color: getCurrentColor('purple'),
  // 배경 색상 shape_data.balloonFillColor
  balloonFillColor: 'rgba(255, 255, 255, 1)',
  // 글씨 색상 shape_data.balloonFontColor
  balloonFontColor: getCurrentColor('purple'),
} as const

export const filterPeriodItems = [
  {
    name: '상반기',
    value: 'firstHalf',
  },
  {
    name: '하반기',
    value: 'secondHalf',
  },
  {
    name: '해빙기',
    value: 'thawPeriod',
  },
  {
    name: '우기',
    value: 'rainySeason',
  },
  {
    name: '동절기',
    value: 'winterSeason',
  },
]

export const filterTypeItems = [
  {
    name: '정기안전점검',
    value: 'regularSafetyInspection',
  },
  {
    name: '정밀안전점검',
    value: 'detailedSafetyInspection',
  },
  {
    name: '정밀안전진단',
    value: 'preciseSafetyDiagnosis',
  },
]

export const getCurrentFilterName = (value: string, type: 'period' | 'type'): string => {
  const findItem =
    type == 'period'
      ? filterPeriodItems.find(item => item.value == value)
      : filterTypeItems.find(item => item.value == value)
  if (!findItem) return ''
  return findItem.name
}

// 개별 버튼 정의
export const BUTTON_DEFINITIONS = {
  shape: {
    id: 'shape',
    text: '모양',
    scale: 1.0,
  },
  fill: {
    id: 'fill',
    text: '채우기',
    scale: 1.0,
  },
  color: {
    id: 'color',
    text: '색상',
    scale: 1.0,
  },
  align: {
    id: 'align',
    text: '정렬',
    scale: 1.0,
  },
  copy: {
    id: 'copy',
    text: '복제',
    scale: 1.0,
  },
  attach: {
    id: 'attach',
    text: '추가',
    scale: 1.0,
  },
  delete: {
    id: 'delete',
    text: '삭제',
    scale: 1.25,
  },
  combine: {
    id: 'combine',
    text: '그룹',
    scale: 1.0,
  },
  ungroup: {
    id: 'ungroup',
    text: '그룹 해제',
    scale: 1.0,
  },
  tripodTail: {
    id: 'tripodTail',
    text: '그룹 꼬리',
    scale: 1.0,
  },
  order: {
    id: 'order',
    text: '번호',
    scale: 1.0,
  },
  becomeCurrent: {
    id: 'becomeCurrent',
    text: '현재차로',
    scale: 1.0,
  },
  becomePrevious: {
    id: 'becomePrevious',
    text: '전차로',
    scale: 1.0,
  },
} as const

export const shapeTypeItems = [
  {
    id: SHAPES.POINT,
    text: '점',
    scale: 1.0,
  },
  {
    id: SHAPES.DIRECTION,
    text: '방향',
    scale: 1.0,
  },
  {
    id: SHAPES.RECT,
    text: '영역',
    scale: 1.0,
  },
]

export type ButtonDefType = (typeof BUTTON_DEFINITIONS)[keyof typeof BUTTON_DEFINITIONS]['text']

// Shape 타입별 버튼 구성
export const SHAPE_BUTTON_CONFIG = {
  // color: 색상, copy: 복사, delete: 삭제, attach: 결함추가, shape: 모양, fill: 채우기, order: 번호
  DEFAULT: ['color', 'copy', 'delete'],
  [SHAPES.POINT]: ['color', 'tripodTail', 'copy', 'attach', 'order', 'delete'],
  [SHAPES.DIRECTION]: ['color', 'copy', 'attach', 'order', 'delete'],
  [SHAPES.RECT]: ['color', 'shape', 'fill', 'copy', 'attach', 'order', 'delete'],
  [SHAPES.BRUSH]: ['delete'],
  [SHAPES.TRIPOD]: ['color', 'ungroup', 'tripodTail', 'order', 'delete'],
  NO_SHAPE: ['delete'],
} as const

export const WIDTH_VALUES_RANGE = [
  '~0.1mm미만',
  '0.1mm이상~0.2mm미만',
  '0.2mm이상~0.3mm미만',
  '0.3mm이상~0.5mm미만',
  '0.5mm이상',
  '직접입력',
]

export const WIDTH_VALUES_DIRECT = [
  '100',
  '200',
  '300',
  '400',
  '500',
  '600',
  '700',
  '800',
  '900',
  '1000',
  '1200',
  '1500',
  '1800',
  '2000',
  '3000',
]

export const LENGTH_VALUES = [
  '100',
  '200',
  '300',
  '400',
  '500',
  '600',
  '700',
  '800',
  '900',
  '1000',
  '1200',
  '1500',
  '1800',
  '2000',
  '3000',
]

// 개수 - 숫자 선택값
export const COUNT_VALUES = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10']

// 개수 - 정량화가 어려운 경우 사용하는 서술형 선택값 (단위 '개'를 붙이지 않는다)
export const COUNT_TEXT_VALUES = ['다수', '전반적']

/**
 * 개수 값이 단위('개')를 붙일 수 없는 서술형인지 판별한다.
 * 미리 정의된 서술형 선택값 외에, 직접 입력된 임의 문자열도 서술형으로 취급한다.
 * @param value - 개수 값
 */
export function isTextCountValue(value: string | number | null | undefined): boolean {
  if (value === null || value === undefined || value === '') return false
  return Number.isNaN(Number(value))
}

export const currentLink = {
  dev: 'https://dev.zentrix.kr/#/',
  prod: 'https://app.zentrix.kr/',
  //테스트용으로 주석처리
  // local: 'http://172.30.1.100:3000/#/',
}

export const sizeScale10Steps = {
  1: 0.5,
  2: 1,
  3: 1.5,
  4: 2,
  5: 2.5,
  6: 3,
  7: 3.5,
  8: 4,
  9: 4.5,
  10: 5,
}
export const sizeScale10StepsZero = {
  1: 0,
  2: 1,
  3: 1.5,
  4: 2,
  5: 2.5,
  6: 3,
  7: 3.5,
  8: 4,
  9: 4.5,
  10: 5,
}

export const reverseSizeScale10Steps = {
  0.5: 1,
  1: 2,
  1.5: 3,
  2: 4,
  2.5: 5,
  3: 6,
  3.5: 7,
  4: 8,
  4.5: 9,
  5: 10,
}
export const reverseSizeScale10StepsZero = {
  0: 1,
  1: 2,
  1.5: 3,
  2: 4,
  2.5: 5,
  3: 6,
  3.5: 7,
  4: 8,
  4.5: 9,
  5: 10,
}

export const getFileExt = (path: string): string => {
  return path.split('.').pop() || ''
}

// 모바일 화면 너비 기준 상수
export const mobileWidth = 768
// 모바일너비인지 ref, 상수는 아니지만 여기에 작성..
export const isMobileWidth = ref(window.innerWidth <= mobileWidth)

// 화면 크기 변경 시 isMobileWidth 업데이트
window.addEventListener('resize', () => {
  isMobileWidth.value = window.innerWidth <= mobileWidth
})

// 보고서 PDF → 전차 등록(report import) 기능 사용 허용 login_id (서버와 동일 조건)
export const REPORT_IMPORT_ALLOWED_LOGIN_IDS: readonly string[] = ['hck2']

// 보고서 PDF 업로드 상한 (bytes). 워커 WORKER_MAX_PDF_MB 기본값과 동일
export const REPORT_IMPORT_MAX_PDF_BYTES = 200 * 1024 * 1024

// 보고서 가져오기 진행 상태 폴링 주기 (ms)
export const REPORT_IMPORT_POLL_INTERVAL_MS = 3000
